/**
 * Générateur — Automatisme : comparaison de fractions.
 * Attendu ID : 1098 | Niveau : 4ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 653 :
 * « Comparaison de fractions. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * EXTENSION de 1052 (5e), pas un wrapper. La ligne nue automatise l'objectif
 * 5e l. 575 : quand aucun des quatre regards de 1052 ne s'applique (même
 * dénominateur, même numérateur, repère 1/2, repère 1), on met le même
 * dénominateur (le geste de 1097) puis on compare les numérateurs. Cas NEUF
 * « commun » : dénominateurs multiples (2/3 et 7/12) ou quelconques simples
 * (5/6 et 3/4, couples de 1097). Les quatre cas de 5e en entretien.
 * Frontières : décimaux (923, 1048), négatifs (l. 662), égalité (1051),
 * trois fractions à ranger : exclus.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 6NB3 « Comparer
 * à 1 », « Mêmes dénominateurs », « Même numérateur » = cas de 5e à bornes
 * trop larges (10-100) ; 6NB4 (entier + fraction) = 1053. Le cas neuf vient
 * du programme.
 *
 * ── Ce qui est RÉUTILISÉ de gen_1052 (exports) ───────────────────────────
 * Les tirages des cas de 5e (coupleOuRepli, avec le discriminant), la ligne
 * de la série flash, les affirmations du Vrai/Faux et leurs codes, les
 * explications. Le cas commun ajoute son tirage, ses codes et le QCM ici.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie, clé de 1052) : quatre
 *     comparaisons sur deux lignes, saisie du seul symbole < ou > (strict) :
 *     deux cas de 5e tirés parmi les quatre, deux cas communs (un multiples,
 *     un quelconque), dont un DISCRIMINANT (la plus grande a le plus petit
 *     numérateur, 2/3 et 7/12) ; la plus grande à gauche deux fois sur quatre.
 *   Carte 2 « Le même dénominateur d'abord » : QCM « Quelle écriture pour
 *     comparer ? » (ecriture_qcm_unique) : « Pour comparer 2/3 et 7/12,
 *     j'écris… » QUATRE couples : 8/12 et 7/12 (clé), 2/12 et 7/12
 *     (pas_converti : les numérateurs recopiés sur le commun), les facteurs
 *     échangés (facteurs_echanges), un fait de table voisin
 *     (voisin_meme_table) ; préféré au QCM de 1052 parce qu'il isole le
 *     geste neuf. Vrai/Faux 3 inégalités (plus_tableau_vf, clé de 1052) :
 *     un cas commun au moins, deux cas de 5e.
 *
 * INVARIANTS du cas commun : dénominateurs multiples (b | d, d ≤ 24) ou
 * couples quelconques de 1097 ; toutes deux < 1 irréductibles, hauts et
 * bas différents, jamais égales, jamais 1/2 ; écart d'au moins 1/24 pour
 * que le commun tranche à l'œil (numérateurs convertis distincts, par
 * construction). Cas de 5e aux bornes de 1052.
 * SACRIFIÉS : les quatre couples de 1052 (importés) et (3/4 ; 5/8), la page
 * neuve de la fiche (« 6/8 et 5/8 : 6 parts contre 5 »), auto-testés.
 *
 * ── Erreurs (mêmes noms qu'en 5e) ────────────────────────────────────────
 *   numerateur_seul   : les hauts comparés comme des entiers, PRINCIPALE ;
 *   denominateur_seul : NEUF, « 3/4 > 5/6 parce que 4 < 6 » (la règle du
 *                       même numérateur appliquée à tort) ;
 *   pas_converti      : le bon commun, les numérateurs recopiés ;
 *   facteurs_echanges : chaque numérateur multiplié par le facteur de
 *                       l'autre ;
 *   voisin_meme_table : la conversion avec un fait de table voisin ;
 *   + les familles de 1052 sur les cas de 5e.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';
import {
    coupleOuRepli, ligneFlash, affirmation, fracTex, signe, codeFaux,
    COUPLES_SACRIFIES as SACRIFIES_1052, EXPLICATIONS as EXPLICATIONS_1052,
} from '../../5eme/nombres-rationnels/gen_1052';
import { COUPLES_QUELCONQUES } from './gen_1097';

const ATTENDU_ID = 1098;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_MEME = 'Le même dénominateur d\'abord';

// ─── Le cas commun ────────────────────────────────────────────────────────

const CAS_5E = ['meme_den', 'meme_num', 'moitie', 'un'];
const DEN_MULTIPLE_MAX = 24;

/** La page neuve de la fiche : 3/4 et 5/8 ne sortent jamais ensemble. */
export const COUPLE_SACRIFIE = [{ a: 3, b: 4 }, { a: 5, b: 8 }];
const memeFraction = (f, g) => f.a === g.a && f.b === g.b;
const estSacrifie = (f, g) => [...SACRIFIES_1052, COUPLE_SACRIFIE].some(([u, v]) => (memeFraction(f, u) && memeFraction(g, v)) || (memeFraction(f, v) && memeFraction(g, u)));

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
const fractionSous1 = (b) => {
    for (let k = 0; k < 40; k++) {
        const a = randInt(1, b - 1);
        if (gcd(a, b) === 1 && 2 * a !== b) return { a, b };
    }
    return { a: 1, b };
};

/** Le commun de deux dénominateurs du cas commun (le grand si multiples, celui du couple sinon). */
const communDe = (b, d) => {
    if (d % b === 0) return d;
    if (b % d === 0) return b;
    const c = COUPLES_QUELCONQUES.find(([u, v]) => (u === b && v === d) || (u === d && v === b));
    return c ? c[2] : null;
};

/**
 * Un couple du cas commun : { x, y, cas: 'commun', sousCas, discriminant, commun }
 * avec x > y ; null si 300 essais échouent.
 */
const tirerCommun = ({ sousCas = randomChoice(['multiples', 'quelconque']), discriminant = null } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        let b;
        let d;
        if (sousCas === 'multiples') {
            b = randInt(2, 8);
            d = b * randInt(2, Math.floor(DEN_MULTIPLE_MAX / b));
            if (d > DEN_MULTIPLE_MAX) continue;
        } else {
            [b, d] = randomChoice(COUPLES_QUELCONQUES);
        }
        const [f, g] = shuffle([fractionSous1(b), fractionSous1(d)]);
        if (f.a === g.a || signe(f, g) === 0 || estSacrifie(f, g)) continue;
        const [x, y] = signe(f, g) > 0 ? [f, g] : [g, f];
        const estDiscriminant = x.a < y.a;
        if (discriminant === true && !estDiscriminant) continue;
        if (discriminant === false && estDiscriminant) continue;
        return { x, y, cas: 'commun', sousCas, discriminant: estDiscriminant, commun: communDe(b, d) };
    }
    return null;
};

/** Replis déterministes vérifiés, hors sacrifiés : 2/3 > 7/12 (multiples, discriminant), 5/6 > 3/4 (quelconque). */
const REPLIS_COMMUN = {
    multiples: { x: { a: 2, b: 3 }, y: { a: 7, b: 12 }, cas: 'commun', sousCas: 'multiples', discriminant: true, commun: 12 },
    quelconque: { x: { a: 5, b: 6 }, y: { a: 3, b: 4 }, cas: 'commun', sousCas: 'quelconque', discriminant: false, commun: 12 },
};
const communOuRepli = (contraintes = {}) => tirerCommun(contraintes) || REPLIS_COMMUN[contraintes.sousCas || 'multiples'];

/** Un couple de n'importe quel cas. */
const tirer = (cas, contraintes = {}) => (cas === 'commun' ? communOuRepli(contraintes) : coupleOuRepli(cas, contraintes));

const EXPLICATIONS = {
    ...EXPLICATIONS_1052,
    denominateur_seul: 'Le plus petit dénominateur ne gagne que si les numérateurs sont les mêmes : ici il faut le même dénominateur d\'abord.',
    pas_converti: 'Quand je change le dénominateur, le numérateur change aussi : 2/3, c\'est 8/12, pas 2/12.',
    facteurs_echanges: 'Chaque fraction est multipliée par SON facteur : 2/3 par 4 (3 × 4 = 12), 7/12 reste.',
    voisin_meme_table: 'Je vérifie le fait de table : 3 × 4 = 12, donc 2/3 = 8/12.',
    regle: 'Même bas : je compare les hauts. Même haut : le plus petit bas gagne. Repère 1/2 ou 1. Sinon, le même dénominateur d\'abord.',
};

/** Le code de la conception qui rendrait vraie l'inégalité FAUSSE d'un couple commun. */
const codeFauxCommun = (l) => {
    if (l.discriminant) return 'numerateur_seul';
    if (l.x.b > l.y.b) return 'denominateur_seul';
    return 'pas_converti';
};

/** « f < g » ou « f > g », vraie ou fausse, pour un couple commun (même écriture que 1052). */
const affirmationCommun = (l, vraie) => {
    const gaucheGrand = randomChoice([true, false]);
    const [g, d] = gaucheGrand ? [l.x, l.y] : [l.y, l.x];
    const symVrai = gaucheGrand ? '>' : '<';
    const sym = vraie ? symVrai : (symVrai === '>' ? '<' : '>');
    return { ligne: tex(`${fracTex(g)} ${sym} ${fracTex(d)}`), code: vraie ? null : codeFauxCommun(l) };
};

const meta = ({ x, y, cas, discriminant, sousCas, commun }) => ({ x, y, cas, discriminant, ...(sousCas ? { sousCas, commun } : {}) });

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code de 1052 ou de 1098 (moitie_oubliee n'en a pas) ; numerateurs_a_l_envers, petit_denominateur_gagne et facteurs_echanges sont neufs. */
const CODE_COMMUN = {
    numerateur_seul: 'numerateur_seul', denominateur_grand: 'denominateur_grand', fraction_sous_1: 'fraction_sous_1',
    a_l_envers: 'numerateurs_a_l_envers', denominateur_seul: 'petit_denominateur_gagne', pas_converti: 'pas_converti',
    facteurs_echanges: 'facteurs_echanges', voisin_meme_table: 'fait_de_table_voisin',
};

const symbole = (u, v) => (u > v ? '>' : '<');

/** Le symbole qu'écrit chaque conception calculable entre g et d (règles du reconnaisseur de l'atelier) : [code, symbole]. */
const conceptions = (g, d) => [
    ...(g.a !== d.a ? [['numerateur_seul', symbole(g.a, d.a)]] : []),
    ...(g.b !== d.b ? [['denominateur_grand', symbole(g.b, d.b)]] : []),
    ...(g.b !== d.b && g.a !== d.a ? [['denominateur_seul', symbole(d.b, g.b)]] : []),
    ...(g.b === d.b && g.a !== d.a ? [['a_l_envers', symbole(d.a, g.a)]] : []),
];

/** Le code du symbole faux entre g et d : celui de la seule conception qui l'écrit, le code du gen compris (pas_converti compare les numérateurs) ; null sinon. */
const codeComparaison = (g, d, faux, codeGen) => {
    const regles = conceptions(g, d);
    const codes = new Set(regles.filter(([, s]) => s === faux).map(([c]) => CODE_COMMUN[c]));
    const regle = regles.find(([c]) => c === (codeGen === 'pas_converti' ? 'numerateur_seul' : codeGen));
    if (codeGen && (!regle || regle[1] === faux)) codes.add(CODE_COMMUN[codeGen] || codeGen);
    const [code] = [...codes];
    return codes.size === 1 && Object.values(CODE_COMMUN).includes(code) ? code : null;
};

/** Le code que le gen donne à la comparaison fausse d'un couple. */
const codeDuGen = (l) => (l.cas === 'commun' ? codeFauxCommun(l) : codeFaux(l));

/** Une inégalité « \dfrac{a}{b} < \dfrac{c}{d} » : [g, symbole, d], null sinon. */
const lireInegalite = (ligne) => {
    const m = ligne.match(/\\dfrac\{(\d+)\}\{(\d+)\} ([<>]) \\dfrac\{(\d+)\}\{(\d+)\}/);
    return m ? [{ a: Number(m[1]), b: Number(m[2]) }, m[3], { a: Number(m[4]), b: Number(m[5]) }] : null;
};

/** Le Vrai/Faux : chaque inégalité fausse porte le code de la seule conception qui la rend vraie. */
const erreursVf = (lignes) => lignes.map(({ ligne, vraie, code }) => {
    const l = !vraie && lireInegalite(ligne);
    return { code: l && codeComparaison(l[0], l[2], l[1], code), ligne };
}).filter((e) => e.code);

/** Les écritures fausses de « f et g » sur le commun L, toutes variantes comprises : [code, couple]. */
const ecrituresFausses = (f, g, L) => {
    const kf = L / f.b;
    const kg = L / g.b;
    const sur = (u, v) => [{ a: u, b: L }, { a: v, b: L }];
    const voisins = [-1, 1].flatMap((dv) => [
        ...(kf > 1 ? [['voisin_meme_table', sur(f.a * (kf + dv), g.a * kg)]] : []),
        ...(kg > 1 ? [['voisin_meme_table', sur(f.a * kf, g.a * (kg + dv))]] : []),
    ]);
    return [['pas_converti', sur(f.a, g.a)], ['facteurs_echanges', sur(f.a * kg, g.a * kf)], ...voisins];
};

/** Le QCM : chaque couple faux porte son code, sauf si une autre famille écrit le même couple. */
const erreursQcm = (f, g, L, faux) => faux
    .filter((x) => ecrituresFausses(f, g, L).every(([c, p]) => !memeCouple(p, x.couple) || CODE_COMMUN[c] === CODE_COMMUN[x.code]))
    .map((x) => ({ code: CODE_COMMUN[x.code], valeur: coupleTex(...x.couple) }));

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète chaque comparaison par &lt; ou &gt;.</b></p>';

const payloadFlash = (items) => {
    const phrase = [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');
    const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items: items.map((l) => ({ ...meta(l), grandAGauche: l.grandAGauche })) };
    const erreurs = items.map((l, trou) => {
        const faux = l.grandAGauche ? '<' : '>';
        return { code: l.grandAGauche ? codeComparaison(l.x, l.y, faux, codeDuGen(l)) : codeComparaison(l.y, l.x, faux, codeDuGen(l)), trou, valeur: faux };
    }).filter((e) => e.code);
    payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const cas5e = shuffle([...CAS_5E]).slice(0, 2);
        const porteur = randomChoice(['multiples', 'quelconque']);
        const lignes = [
            ...cas5e.map((cas) => coupleOuRepli(cas, {})),
            communOuRepli({ sousCas: 'multiples', discriminant: porteur === 'multiples' ? true : null }),
            communOuRepli({ sousCas: 'quelconque', discriminant: porteur === 'quelconque' ? true : null }),
        ];
        if (lignes.some((l) => !l)) continue;
        if (!lignes.some((l) => l.cas === 'commun' && l.discriminant)) continue;
        const cotes = shuffle([true, true, false, false]);
        return payloadFlash(shuffle(lignes).map((l, i) => ({ ...l, grandAGauche: cotes[i] })));
    }
    return payloadFlash([
        { ...coupleOuRepli('meme_den', {}), grandAGauche: true },
        { ...coupleOuRepli('un', {}), grandAGauche: false },
        { ...REPLIS_COMMUN.multiples, grandAGauche: true },
        { ...REPLIS_COMMUN.quelconque, grandAGauche: false },
    ]);
};

// ─── Carte 2, format A : QCM « Quelle écriture pour comparer ? » ──────────

const coupleTex = (u, v) => `${tex(fracTex(u))} et ${tex(fracTex(v))}`;
const memeCouple = (p, q) => memeFraction(p[0], q[0]) && memeFraction(p[1], q[1]);

const genQcm = () => {
    for (let essai = 0; essai < 120; essai++) {
        const l = communOuRepli({ sousCas: randomChoice(['multiples', 'quelconque']) });
        const [f, g] = randomChoice([[l.x, l.y], [l.y, l.x]]);
        const L = l.commun;
        const kf = L / f.b;
        const kg = L / g.b;
        const bon = [{ a: f.a * kf, b: L }, { a: g.a * kg, b: L }];
        const dv = randomChoice([-1, 1]);
        const faux = [
            { couple: [{ a: f.a, b: L }, { a: g.a, b: L }], code: 'pas_converti' },
            { couple: [{ a: f.a * kg, b: L }, { a: g.a * kf, b: L }], code: 'facteurs_echanges' },
            { couple: kf > 1 ? [{ a: f.a * (kf + dv), b: L }, { a: g.a * kg, b: L }] : [{ a: f.a * kf, b: L }, { a: g.a * (kg + dv), b: L }], code: 'voisin_meme_table' },
        ];
        const tous = [bon, ...faux.map((x) => x.couple)];
        if (tous.some((p) => p.some((u) => u.a < 1))) continue;
        if (tous.some((p, i) => tous.some((q, j) => j > i && memeCouple(p, q)))) continue;
        const choix = tous.map(([u, v]) => coupleTex(u, v));
        const payload = makeQcm(choix, choix[0], `<p><b>Pour comparer ${coupleTex(f, g)}, j'écris d'abord :</b></p>`, opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...meta(l), gauche: f, droite: g, distracteurs: faux.map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: erreursQcm(f, g, L, faux) };
        return payload;
    }
    // Repli vérifié : 2/3 et 7/12 → 8/12 et 7/12 ; 2/12 et 7/12 ; 2/12 et 28/12 ; 10/12 et 7/12.
    const l = REPLIS_COMMUN.multiples;
    const choix = [coupleTex({ a: 8, b: 12 }, { a: 7, b: 12 }), coupleTex({ a: 2, b: 12 }, { a: 7, b: 12 }), coupleTex({ a: 2, b: 12 }, { a: 28, b: 12 }), coupleTex({ a: 10, b: 12 }, { a: 7, b: 12 })];
    const payload = makeQcm(choix, choix[0], `<p><b>Pour comparer ${coupleTex(l.x, l.y)}, j'écris d'abord :</b></p>`, opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...meta(l), gauche: l.x, droite: l.y, distracteurs: [{ code: 'pas_converti' }, { code: 'facteurs_echanges' }, { code: 'voisin_meme_table' }] };
    const fauxRepli = [[2, 7, 'pas_converti'], [2, 28, 'facteurs_echanges'], [10, 7, 'voisin_meme_table']]
        .map(([u, v, code]) => ({ couple: [{ a: u, b: 12 }, { a: v, b: 12 }], code }));
    payload.contenu = { ...payload.contenu, erreurs: erreursQcm(l.x, l.y, 12, fauxRepli) };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 inégalités, un cas commun au moins) ─

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const cas = ['commun', ...shuffle([...CAS_5E]).slice(0, 2)];
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // une fausse au moins que « numérateur seul » jugerait vraie : un couple discriminant déclaré à l'envers
        const iFaux = verites.indexOf(false);
        const couples = cas.map((c, i) => {
            const discriminable = c === 'commun' || c === 'moitie' || c === 'un';
            return tirer(c, i === iFaux && discriminable ? { discriminant: true } : {});
        });
        if (couples.some((c) => !c)) continue;
        if (!couples.some((c, i) => !verites[i] && c.discriminant)) continue;
        const affs = couples.map((c, i) => (c.cas === 'commun' ? affirmationCommun(c, verites[i]) : affirmation(c, verites[i])));
        const lignes = shuffle(affs.map((a, i) => ({ ...a, vraie: verites[i] })));
        if (new Set(lignes.map((x) => x.ligne)).size < 3) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.ligne), lignes.map((x) => (x.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { couples: couples.map(meta), verites: lignes.map((x) => x.vraie), distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: erreursVf(lignes) };
        return payload;
    }
    // Repli vérifié : 7/12 > 2/3 faux (numerateur_seul) ; 7/9 > 4/9 vrai ; 5/9 < 5/14 faux (denominateur_grand).
    const couples = [REPLIS_COMMUN.multiples, coupleOuRepli('meme_den', {}), coupleOuRepli('meme_num', {})];
    const lignes = [
        tex(`${fracTex({ a: 7, b: 12 })} > ${fracTex({ a: 2, b: 3 })}`),
        tex(`${fracTex({ a: 7, b: 9 })} > ${fracTex({ a: 4, b: 9 })}`),
        tex(`${fracTex({ a: 5, b: 9 })} < ${fracTex({ a: 5, b: 14 })}`),
    ];
    const payload = makeVraiFaux(lignes, [1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { couples: couples.map(meta), verites: [false, true, false], distracteurs: [{ code: 'numerateur_seul' }, { code: 'denominateur_grand' }] };
    const codesRepli = ['numerateur_seul', null, 'denominateur_grand'];
    payload.contenu = { ...payload.contenu, erreurs: erreursVf(lignes.map((ligne, i) => ({ ligne, vraie: !codesRepli[i], code: codesRepli[i] }))) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la clé est une ÉCRITURE (les deux fractions sur le commun), les distracteurs trois écritures fausses aux couples deux à deux distincts ; collision → nouveau tirage',
        'le produit des dénominateurs (8/12 et 7/12 contre 24/36 et 21/36) n\'est jamais un distracteur : c\'est une écriture juste',
        'sacrifiés : les quatre couples de 1052 (importés) et 3/4 avec 5/8',
    ],
    qcm_unique: {
        support: 'un couple du cas commun : dénominateurs multiples (b | d, d ≤ 24) ou quelconques simples (couples de 1097)',
        familles: {
            pas_converti: 'les numérateurs recopiés sur le commun (2/12 et 7/12)',
            facteurs_echanges: 'chaque numérateur multiplié par le facteur de l\'autre',
            voisin_meme_table: 'un fait de table voisin (2/3 → 10/12)',
        },
    },
    vf_tableau_vf: 'trois inégalités, un cas commun au moins ; fausses : numerateur_seul (couple discriminant), denominateur_seul (neuf), pas_converti, et les familles de 1052 sur les cas de 5e',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    ecriture_qcm_unique: genQcm,
    plus_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme', label: L_MEME, formatsList: [
        { key: 'ecriture_qcm_unique', label: 'QCM' },
        { key: 'plus_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    ecriture_qcm_unique: ['fait_de_table_voisin'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
